import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_INTERAGENCY_CHAT,
  SOURCE_RCS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_TRANSFER,
  CONVERSATION_TRANSFER_UPDATE,
  CONVERSATION_UPDATE,
} from '@rsos/hermes-js/src/typings/conversation';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import {
  addConversation,
  transferConversation,
  receiveTransferredConversation,
  updateConversation,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { addRCSConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { dismissToastByID } from '@rsos/utils/toastUtils';
import { addLocToQueue } from '../../../actions';
import useHasAccessToAdminLineRequests from '../../../hooks/useHasAccessToAdminLineRequests';
import { interagencyAPI } from '../../../reduxDataStore/interagency/interagencyAPI';
import { convertKeysToCamelCase } from './helpers';

// Shared body shape for call-queue lifecycle messages (start, update, end).
const callQueueBodySchema = z
  .object({
    source: z.enum([SOURCE_ADMIN_SMS, SOURCE_RCS, SOURCE_TCAPI]),
    application: z.string(),
    caller_id: z.string().nullable(),
    conversation_start_time: z.number(),
    conversation_id: z.string(),
    valid_for_psap: z.boolean().nullable(),
    extra_info: z.record(z.string(), z.unknown()).default({}),
    location_data: z.unknown().optional(),
    session_metadata: z.unknown().optional(),
  })
  .passthrough();

const interagencyBodySchema = z
  .object({
    source: z.literal(SOURCE_INTERAGENCY_CHAT),
    application: z.string(),
    conversation_id: z.string(),
    conversation_name: z.string(),
    conversation_start_time: z.number(),
    caller_id: z.string().nullable(),
    valid_for_psap: z.boolean().nullable(),
    /**
     * @deprecated Contains nested participants, status, timestamps etc.
     * When building interagency chat features, elevate the fields you
     * need to the top level of this schema with proper zod types instead
     * of digging into this untyped bag.
     */
    extra_info: z.unknown(),
  })
  .passthrough();

const lifecycleBodySchema = z.union([
  callQueueBodySchema,
  interagencyBodySchema,
]);

const transferBodySchema = z
  .object({
    caller_id: z.string(),
    // null for pending transfers to the receiving agency; set after
    // acceptance in a follow-up message
    conversation_id: z.string().nullable(),
    transfer_status: z.enum(['pending', 'accepted', 'timed_out']),
    initiating_authority_display_name: z.string().optional(),
    initiating_authority_account_id: z.string().optional(),
    receiving_authority_display_name: z.string().optional(),
    receiving_authority_account_id: z.string().optional(),
  })
  .passthrough();

// z.discriminatedUnion matches exactly one branch based on the literal
// value of the 'type' field. If the incoming message's type doesn't
// match any branch, parsing fails — so only the message types listed
// here are handled.
const conversationMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(CONVERSATION_START), body: lifecycleBodySchema }),
  z.object({ type: z.literal(CONVERSATION_UPDATE), body: lifecycleBodySchema }),
  z.object({ type: z.literal(CONVERSATION_END), body: lifecycleBodySchema }),
  z.object({
    type: z.literal(CONVERSATION_TRANSFER),
    body: transferBodySchema,
  }),
  z.object({
    type: z.literal(CONVERSATION_TRANSFER_UPDATE),
    body: transferBodySchema,
  }),
]);

type ConversationWsMessage = z.infer<typeof conversationMessageSchema>;

function isTransferMessage(
  msg: ConversationWsMessage,
): msg is Extract<
  ConversationWsMessage,
  { type: typeof CONVERSATION_TRANSFER | typeof CONVERSATION_TRANSFER_UPDATE }
> {
  return (
    msg.type === CONVERSATION_TRANSFER ||
    msg.type === CONVERSATION_TRANSFER_UPDATE
  );
}

const normalizeLocation = (body: z.infer<typeof callQueueBodySchema>) => {
  const baseLocation = {
    caller_id: body.caller_id,
    call_start_time: body.conversation_start_time,
    source: body.source,
  };

  if (body.location_data && typeof body.location_data === 'object') {
    return { ...baseLocation, ...body.location_data };
  }

  return baseLocation;
};

const useHandleConversationMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const hasAdminLineRequests = useHasAccessToAdminLineRequests();
  const currentPSAPAccountId = useCurrentPSAPAccountID();
  const handleConversationMessage = useCallback(
    // StreamWS callback receives untyped messages; zod validates below
    (message: Record<string, unknown>) => {
      const parsed = conversationMessageSchema.safeParse(message);
      if (!parsed.success) {
        return;
      }
      const msg = parsed.data;

      if (msg.body.source === SOURCE_ADMIN_SMS && !hasAdminLineRequests) {
        return;
      }

      if (isTransferMessage(msg)) {
        const transfer = {
          status: msg.body.transfer_status,
          receivingAuthorityDisplayName:
            msg.body.receiving_authority_display_name ?? null,
          receivingAuthorityAccountId:
            msg.body.receiving_authority_account_id ?? null,
          initiatingAuthorityDisplayName:
            msg.body.initiating_authority_display_name ?? null,
          initiatingAuthorityAccountId:
            msg.body.initiating_authority_account_id ?? null,
        };

        const isReceivingAgency = Boolean(
          msg.body.receiving_authority_account_id &&
            currentPSAPAccountId &&
            msg.body.receiving_authority_account_id === currentPSAPAccountId,
        );

        if (isReceivingAgency) {
          dispatch(
            addLocToQueue({
              caller_id: msg.body.caller_id,
              source: SOURCE_TCAPI,
              call_start_time: Date.now(),
            }),
          );
          dispatch(
            receiveTransferredConversation({
              callerID: msg.body.caller_id,
              transfer,
            }),
          );
        } else {
          dispatch(
            transferConversation({
              callerID: msg.body.caller_id,
              transfer,
            }),
          );
        }
        return;
      }

      if (msg.body.source === SOURCE_INTERAGENCY_CHAT) {
        // TODO(SOUP-1112): Handle interagency chat lifecycle messages
        // eslint-disable-next-line no-console -- debug logging for interagency chat development
        console.debug('Received interagency chat message:', msg);

        // Force refetch of the conversations list via tag invalidation
        dispatch(
          interagencyAPI.util.invalidateTags(['InteragencyConversations']),
        );

        return;
      }

      // At this point source is narrowed to a call-queue source (admin_sms,
      // rcs, tcapi) and transfers/interagency have already returned above.
      // caller_id should always be present for these sources.
      const callQueueSources = [SOURCE_ADMIN_SMS, SOURCE_RCS, SOURCE_TCAPI];
      if (!msg.body.caller_id || !callQueueSources.includes(msg.body.source)) {
        // eslint-disable-next-line no-console -- Defensive check: if we reach here, the narrowing logic above has a gap
        console.error(
          'Unexpected state in conversation handler: expected a call-queue source with caller_id but got',
          { source: msg.body.source, caller_id: msg.body.caller_id },
        );
        return;
      }
      dispatch(addLocToQueue(normalizeLocation(msg.body)));

      let payload = msg.body;
      if (
        msg.body.source === SOURCE_TCAPI &&
        msg.type === CONVERSATION_UPDATE
      ) {
        dismissToastByID(`conversations-toast-${msg.body.caller_id}`);
      }

      if (msg.body.source === SOURCE_TCAPI) {
        const snakeCaseExtraInfo = convertKeysToCamelCase(msg.body.extra_info);
        payload = { ...msg.body, ...snakeCaseExtraInfo };
      }

      if (msg.body.source === SOURCE_RCS && msg.body.session_metadata) {
        dispatch(addRCSConversation(msg.body.session_metadata));
      } else if (msg.type === CONVERSATION_START) {
        dispatch(addConversation(payload));
      } else if (
        msg.type === CONVERSATION_UPDATE ||
        msg.type === CONVERSATION_END
      ) {
        dispatch(updateConversation({ body: payload, messageType: msg.type }));
      }
    },
    [dispatch, hasAdminLineRequests, currentPSAPAccountId],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleConversationMessage,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleConversationMessage');
      };
    }
  }, [StreamWS, handleConversationMessage]);
};

export default useHandleConversationMessage;
